'use client';

import { Suspense, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import toast from 'react-hot-toast';
import { useAuth } from '../../../lib/hooks/useAuth';
import { AuthBackground } from '../../../components/auth/AuthBackground';
import { AuthHeader } from '../../../components/auth/AuthHeader';
import { AuthForm } from '../../../components/auth/AuthForm';
import { AuthModeSwitch } from '../../../components/auth/AuthModeSwitch';
import { authApi } from '../../../lib/api';
import { ROUTES, UI } from '../../../lib/constants';
import logo from '../../../assets/logo.svg';

function PendingVerification({ email }: { email: string }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleResend() {
    setResending(true);
    try {
      await authApi.resendVerification({ email });
      setResent(true);
    } catch (err) {
      console.error('[PendingVerification] Resend failed:', err);
      toast.error('Could not resend — please try again.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center px-4" style={{ backgroundColor: 'var(--color-bg)' }}>
      <AuthBackground />
      <div className="relative w-full max-w-md text-center">
        <div className="mb-10 flex flex-col items-center gap-2">
          <Link href="/home" className="flex flex-col items-center gap-2">
            <Image src={logo} alt="Apporb" width={48} height={48} className="rounded-2xl" />
            <span className="text-sm font-semibold tracking-widest uppercase" style={{ color: 'var(--color-primary)' }}>
              {UI.appName}
            </span>
          </Link>
        </div>

        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5"
          style={{ background: 'linear-gradient(135deg, #e8f0fe, #d3e2fd)' }}
        >
          <svg className="w-7 h-7 text-[#0b57d0]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
        </div>

        <h1 className="text-3xl font-light text-gray-800 mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          Check your inbox
        </h1>
        <p className="text-sm text-gray-400 mb-8 leading-relaxed">
          We sent a verification link to <strong className="text-gray-600">{email}</strong>.
          Click the link to activate your account.
        </p>

        {resent ? (
          <p className="text-sm font-medium mb-6" style={{ color: 'var(--color-primary)' }}>
            New verification email sent!
          </p>
        ) : (
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="w-full py-3 rounded-full text-sm font-semibold text-white transition-opacity disabled:opacity-60 mb-4"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>
        )}

        <p className="text-sm text-gray-400">
          Already verified?{' '}
          <Link href={ROUTES.login} className="font-medium hover:underline" style={{ color: 'var(--color-primary)' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function RegisterForm() {
  const { loading, form, setField, handleSubmit, pendingVerification, registeredEmail } = useAuth('register');

  if (pendingVerification) {
    return <PendingVerification email={registeredEmail} />;
  }

  return (
    <div className="flex h-full items-center justify-center px-4" style={{ backgroundColor: 'var(--color-bg)' }}>
      <AuthBackground />
      <div className="relative w-full max-w-md">
        <AuthHeader mode="register" />
        <AuthForm mode="register" loading={loading} form={form} setField={setField} onSubmit={handleSubmit} />
        <AuthModeSwitch mode="register" />
      </div>
    </div>
  );
}

export default function RegisterPage() {
  return (
    <Suspense>
      <RegisterForm />
    </Suspense>
  );
}
